import * as React from 'react'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Flex } from '@buffer-mono/popcorn'
import { BufferTrackerReact as BufferTracker } from '@buffer-mono/tracking-plan'

import { useAccount } from '~publish/contexts/AccountContext'
import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'
import { hasDismissedObject } from '~publish/legacy/utils/dismissedObjects/hasDismissedObject'

import type { Checklist } from '../../types'
import { actions as welcomeChecklistRefetchOperations } from '../../reducer'
import { useChecklists } from '../hooks/useChecklists'
import { selectChecklist } from '../utils/selectChecklist'
import { getChecklistProgress } from '../utils/getChecklistProgress'
import ChecklistHeader from '../ChecklistHeader'
import ChecklistBody from '../ChecklistBody'
import ChecklistFooter from '../ChecklistFooter'
import { useWelcomeCheckListActions } from '../hooks/useWelcomeChecklistActions'
import { WelcomeCheckListPopover } from './WelcomeCheckListPopover'

import styles from './WelcomeChecklist.module.css'

const COMPLETED_PROGRESS = 100
const CHECKLIST_MODAL_DIMISSED_LOCAL_STORAGE_KEY =
  'isWelcomeChecklistModalDismissed'
const CHECKLIST_COMPLETE_MODAL_DIMISSED_LOCAL_STORAGE_KEY =
  'isChecklistCompleteModalDismissed'
const WELCOME_CHECKLIST_COMPLETE = 'welcome_checklist_complete'
const WELCOME_CHECKLIST_SUPPRESS_MODAL = 'welcome_checklist_suppress_modal'

export const WelcomeChecklist = (): JSX.Element | null => {
  const { account } = useAccount()
  const dispatch = useDispatch()
  const { checklists, loading, error, refetch } = useChecklists()
  const { callWelcomeChecklistAction } = useWelcomeCheckListActions()
  const [dismissedWelcomeChecklist, setDismissedWelcomeChecklist] =
    React.useState(false)
  const popoverBanner = useDismissableBanner('welcome-checklist-popover')

  // Check if we should refetch the checklist status based on app state
  // Check if we should refetch the checklist status based on app state
  const shouldRefetch = useSelector(
    // @ts-expect-error TS(2339) FIXME: Property 'welcomeChecklist' does not exist on type... Remove this comment to see the full error message
    (state) => state.welcomeChecklist.shouldRefetch,
  )

  React.useEffect(() => {
    const dismissed =
      sessionStorage.getItem(CHECKLIST_MODAL_DIMISSED_LOCAL_STORAGE_KEY) ===
      'dismissed'
    setDismissedWelcomeChecklist(dismissed)
  }, [])

  useEffect(() => {
    if (shouldRefetch) {
      refetch()
      dispatch(welcomeChecklistRefetchOperations.clearShouldRefetch())
    }
  }, [dispatch, refetch, shouldRefetch, account])

  let checklist: Checklist | null = null
  let checklistProgress = {
    itemsCompleted: 0,
    itemsTotal: 0,
    progressPercent: 0,
  }
  if (checklists) {
    checklist = selectChecklist(checklists)
    checklistProgress = checklist
      ? getChecklistProgress(checklist)
      : checklistProgress
  }

  const handleChecklistItemClick = (
    id: number,
    checklistItemDescription: string,
    isCompleted: boolean,
  ): void => {
    // Don't allow clicks on completed items
    if (isCompleted) {
      return
    }
    const eventData = {
      organizationId: account?.currentOrganization?.id || '',
      checklistId: checklist?.id || 0,
      checklistItem: checklistItemDescription || '',
      isCompleted: isCompleted || false,
      qtyItemsCompleted: checklistProgress.itemsCompleted,
    }

    BufferTracker.checklistItemClicked(eventData)
    popoverBanner.dismiss()
    callWelcomeChecklistAction(id)
  }

  const onDismissWelcomeChecklist = (): void => {
    sessionStorage.setItem(
      CHECKLIST_MODAL_DIMISSED_LOCAL_STORAGE_KEY,
      'dismissed',
    )
    popoverBanner.dismiss()
    setDismissedWelcomeChecklist(true)
  }

  const dismissedChecklist = hasDismissedObject({
    account,
    objectId: WELCOME_CHECKLIST_COMPLETE,
  })
  const isChecklistCompleteModalDismissed =
    dismissedChecklist ||
    !!localStorage.getItem(CHECKLIST_COMPLETE_MODAL_DIMISSED_LOCAL_STORAGE_KEY)

  // NOTE: After geid-first-post-celebration experiment is complete - we can remove the useEffect below, but keep the modalSuppressed value here
  // to ensure that the modal stays suppressed for users who have already auto-dismissed it via this experiment
  const modalSuppressed = hasDismissedObject({
    account,
    objectId: WELCOME_CHECKLIST_SUPPRESS_MODAL,
  })

  const isChecklistComplete =
    checklistProgress.progressPercent === COMPLETED_PROGRESS

  useEffect(() => {
    const { MODALS, actions } = window?.appshell || {}
    if (
      actions &&
      isChecklistComplete &&
      !isChecklistCompleteModalDismissed &&
      !modalSuppressed &&
      !dismissedWelcomeChecklist
    ) {
      actions.openModal(MODALS.checklistComplete)
    }
  }, [isChecklistComplete, isChecklistCompleteModalDismissed])

  // Hide the checklist if:
  // - The account is not loaded
  // - The checklist complete modal has been dismissed
  // - The checklist is complete
  // - The welcome checklist has been dismissed
  // - There is no checklist data
  // - The checklist is still loading
  // - There was an error fetching the checklist
  const shouldHideChecklist =
    !account?.id ||
    isChecklistCompleteModalDismissed ||
    isChecklistComplete ||
    dismissedWelcomeChecklist ||
    !checklist ||
    loading ||
    error

  useEffect(() => {
    if (!shouldHideChecklist && account?.currentOrganization?.id) {
      BufferTracker.welcomeChecklistViewed({
        organizationId: account?.currentOrganization?.id,
        qtyItemsCompleted: checklistProgress.itemsCompleted,
      })
    }
  }, [
    account?.currentOrganization?.id,
    checklistProgress.itemsCompleted,
    shouldHideChecklist,
  ])

  // Duplicate checking of checklist because Typescript will complain below
  if (shouldHideChecklist || !checklist) {
    return null
  }

  return (
    <WelcomeCheckListPopover banner={popoverBanner}>
      <Flex
        direction="column"
        align="stretch"
        justify="start"
        className={styles.welcomeChecklist}
        data-testid="welcome_checklist"
      >
        <ChecklistHeader
          checklist={checklist}
          checklistProgress={checklistProgress}
          onDismissWelcomeChecklist={onDismissWelcomeChecklist}
        />
        <ChecklistBody
          checklist={checklist}
          handleChecklistItemClick={handleChecklistItemClick}
        />
        <ChecklistFooter />
      </Flex>
    </WelcomeCheckListPopover>
  )
}

export default WelcomeChecklist
